/**
 * Motion tokens for the site-wide motion system.
 *
 * Every value here is fixed by the design spec so no call site has to invent
 * one. `SPRING.scrub` is copied from PortalCarousel's existing SMOOTH rather
 * than chosen fresh, so new scrubbing feels like the carousel this work is
 * modelled on.
 */

/** Three intensities. The contract is behavioural, not cosmetic: at "quiet" a
    primitive does nothing at all - no listeners, no rAF. */
export type MotionLevel = "loud" | "medium" | "quiet";

/** Seconds, for framer-motion transitions. */
export const DURATION = {
  instant: 0.12,
  quick: 0.2,
  base: 0.32,
  slow: 0.56,
} as const;

/** The expo-out curve this codebase already uses everywhere. */
export const EASE = {
  standard: [0.16, 1, 0.3, 1] as const,
};

export const SPRING = {
  scrub: { stiffness: 100, damping: 30, restDelta: 0.001 },
} as const;

/** Pixels. */
export const TRAVEL = { sm: 8, md: 16, lg: 34 } as const;

export const TILT_MAX_DEG: Record<MotionLevel, number> = {
  loud: 8,
  medium: 0,
  quiet: 0,
};

export const MAGNET_PULL_PX: Record<MotionLevel, number> = {
  loud: 6,
  medium: 3,
  quiet: 0,
};

export const AMBIENT_AMPLITUDE: Record<MotionLevel, number> = {
  loud: 1,
  medium: 0.5,
  quiet: 0,
};

/**
 * Sibling offset, in seconds.
 *
 * The gap between one item in a group starting and the next. This is the
 * "offset and delay" principle in one number: siblings that arrive together
 * read as a block being switched on, siblings offset by a beat read as a
 * sequence someone authored.
 *
 * 60ms is not a fresh choice — it is the value already hand-written at three
 * call sites (Trust's cards, HomeFaq's rows, Highlights' cards). Naming it
 * makes it one decision instead of three copies that can drift apart.
 *
 * Below ~40ms the offset stops being legible and just softens the edge of a
 * simultaneous entrance; above ~90ms a row of five starts to feel like it is
 * being dealt out rather than arriving.
 */
export const STAGGER = 0.06;

/**
 * Ceiling on accumulated stagger, in seconds.
 *
 * Without a cap, `index * STAGGER` means the twelfth item in a list waits
 * two-thirds of a second after the first — long enough that a visitor who
 * scrolled to read it watches it arrive instead of finding it already there.
 * Past this point items share the last slot rather than queueing further.
 */
export const STAGGER_MAX = 0.3;

/** Offset for item `index` in a group, held under `STAGGER_MAX`. */
export function staggerDelay(index: number): number {
  return Math.min(index * STAGGER, STAGGER_MAX);
}
